import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Badge, Empty, Skeleton, useToast } from '../components/ui';
import { ApiError, api, type TeamMember } from '../lib/api';
import { hm } from '../lib/format';

const WEEKDAYS = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];

export function TeamPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<TeamMember[]>('/admin/users'),
    refetchInterval: 60_000,
  });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/admin/users/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team'] });
      toast('Alteração salva e registrada na auditoria.', 'success');
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });

  const active = (data ?? []).filter((u) => u.status === 'ACTIVE').length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Equipe</h1>
          <p>
            {active === 1 ? '1 estagiário ativo' : `${active} estagiários ativos`}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="card">
          <div className="card__body" style={{ display: 'grid', gap: 10 }}>
            <Skeleton height={56} />
            <Skeleton height={56} />
          </div>
        </div>
      ) : !data || data.length === 0 ? (
        <div className="card">
          <Empty title="Nenhum usuário ainda" hint="Eles aparecem aqui no primeiro login." />
        </div>
      ) : (
        <section className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Pessoa</th>
                  <th className="num">Meta diária</th>
                  <th>Dias</th>
                  <th>Situação</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.map((u) => (
                  <MemberRow
                    key={u.id}
                    member={u}
                    onPatch={(body) => patch.mutate({ id: u.id, body })}
                    busy={patch.isPending}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function MemberRow({
  member,
  onPatch,
  busy,
}: {
  member: TeamMember;
  onPatch: (body: Record<string, unknown>) => void;
  busy: boolean;
}) {
  return (
    <tr style={member.status === 'INACTIVE' ? { opacity: 0.55 } : undefined}>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={member.name} src={member.avatarUrl} />
          <div>
            <div style={{ fontWeight: 700 }}>{member.name}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--faint)' }}>{member.email}</div>
          </div>
        </div>
      </td>

      <td className="num">
        <input
          type="number"
          min={0}
          max={1440}
          step={30}
          defaultValue={member.dailyTargetMinutes}
          disabled={busy}
          style={{ width: 92, textAlign: 'right' }}
          onBlur={(e) => {
            const v = Number(e.target.value);
            if (v !== member.dailyTargetMinutes) onPatch({ dailyTargetMinutes: v });
          }}
        />
        <div style={{ fontSize: '0.74rem', color: 'var(--faint)' }}>
          {hm(member.dailyTargetMinutes)}/dia
        </div>
      </td>

      <td>
        <div style={{ display: 'flex', gap: 3 }}>
          {WEEKDAYS.map((label, i) => {
            const day = i + 1;
            const on = member.workDays.includes(day);
            return (
              <button
                key={day}
                type="button"
                title={`Alternar dia ${day}`}
                disabled={busy}
                onClick={() =>
                  onPatch({
                    workDays: on
                      ? member.workDays.filter((d) => d !== day)
                      : [...member.workDays, day].sort(),
                  })
                }
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  border: '1px solid var(--line)',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  background: on ? 'var(--vj-indigo)' : 'var(--surface)',
                  color: on ? '#fff' : 'var(--faint)',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </td>

      <td>
        <Badge tone={member.status === 'ACTIVE' ? 'ok' : 'danger'}>
          {member.status === 'ACTIVE' ? 'ativo' : 'inativo'}
        </Badge>
      </td>

      <td>
        <button
          className="btn btn--ghost btn--sm"
          disabled={busy}
          onClick={() =>
            onPatch({ status: member.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' })
          }
        >
          {member.status === 'ACTIVE' ? 'Desativar' : 'Reativar'}
        </button>
      </td>
    </tr>
  );
}
